import type { DashboardData } from '../../types/dashboard'
import type { ExposureParameters } from '../../types/portfolio'
import { safeDivide, safeTypeNumber } from '../../utils/calculationHelper'
/**
 * Represents the location of columns in the claims data.
 */
type ClaimsColumnLocation = {
  [key: string]: number;
};

export default class ClaimsCalculation {
  data: DashboardData
  columns: ClaimsColumnLocation
  claimsType: string[]
  commissionColumns: string[]

  /**
   * Creates an instance of ClaimsCalculation.
   * @param data - The claims data.
   * @param columns - The column locations in the claims data.
   * @param claimsType - The types of claims.
   */
  constructor(data: DashboardData, columns: ClaimsColumnLocation, claimsType: string[]) {
    this.data = data
    this.columns = columns
    this.claimsType = claimsType
    this.commissionColumns = Object.keys(this.columns).filter((key) => key.includes('uw_data.COM'))
  }

  private isWrittenUw(uwAcc: string, underwritingLossRatios: string): boolean {
    return underwritingLossRatios === 'Written' && uwAcc === 'uw'
  }

  private seasonalityCol(ct: string, uwAcc: string, underwritingLossRatios: string): string {
    return this.isWrittenUw(uwAcc, underwritingLossRatios)
      ? `uw_data.${ct}_written_seasonality`
      : `uw_data.${ct}_seasonality`
  }

  private rawSeasonality(
    idx: number,
    ct: string,
    uwAcc: string,
    underwritingLossRatios: string
  ): number {
    const dataRow = this.data[idx]
    const key = this.seasonalityCol(ct, uwAcc, underwritingLossRatios)
    const v = safeTypeNumber(
      dataRow[this.columns[key]] ?? dataRow[this.columns[`uw_data.${ct}_seasonality`]]
    )
    return v > 0 ? v : 1
  }

  private seasonalityValue(
    idx: number,
    ct: string,
    uwAcc: string,
    underwritingLossRatios: string
  ): number {
    const dataRow = this.data[idx]
    const key = this.seasonalityCol(ct, uwAcc, underwritingLossRatios)
    const colIdx = this.columns[key] ?? this.columns[`uw_data.${ct}_seasonality`]
    return safeTypeNumber(dataRow[colIdx])
  }

  private appliedSeasonality(
    idx: number,
    ct: string,
    uwAcc: string,
    underwritingLossRatios: string,
    seasonFactor: boolean
  ): number {
    if (!seasonFactor) return 1
    return this.rawSeasonality(idx, ct, uwAcc, underwritingLossRatios)
  }

  exposure(idx: number, exposureObj: ExposureParameters): number {
    if (exposureObj.method == 'avg') {
      if (safeTypeNumber(this.data[idx][this.columns['uw_data.exposure.sum.count.' + exposureObj.name]]) == 0) {
        return 0
      }

      return (
        safeTypeNumber(
          this.data[idx][this.columns['uw_data.exposure.' + exposureObj.method + '.' + exposureObj.name]]
        ) / safeTypeNumber(this.data[idx][this.columns['uw_data.exposure.sum.count.' + exposureObj.name]])
      )
    }

    return safeTypeNumber(
      this.data[idx][this.columns['uw_data.exposure.' + exposureObj.method + '.' + exposureObj.name]]
    )
  }

  averageGWP(idx: number): number {
    const dataRow = this.data[idx]
    return safeDivide(
      safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']]),
      safeTypeNumber(dataRow[this.columns['uw_data.exposure.sum.Policy Count']])
    )
  }

  /**
   * Calculates the total paid amount for a given index.
   * @param idx - The index of the data row.
   * @returns The total paid amount.
   */
  paidTotal(idx: number): number {
    return this.claimsType.reduce((ps: number, claims: string) => ps + this.paid(idx, claims), 0)
  }

  /**
   * Calculates the paid amount for a given index and claims type.
   * @param idx - The index of the data row.
   * @param claims - The type of claims.
   * @returns The paid amount.
   */
  paid(idx: number, claims: string): number {
    const dataRow = this.data[idx]

    return safeTypeNumber(dataRow[this.columns['claims_data.' + claims + '_paid']])
  }

  /**
   * Calculates the total outstanding amount for a given index.
   * @param idx - The index of the data row.
   * @returns The total outstanding amount.
   */
  osTotal(idx: number): number {
    return this.claimsType.reduce((ps: number, claims: string) => ps + this.os(idx, claims), 0)
  }

  /**
   * Calculates the outstanding amount for a given index and claims type.
   * @param idx - The index of the data row.
   * @param claims - The type of claims.
   * @returns The outstanding amount.
   */
  os(idx: number, claims: string): number {
    const dataRow = this.data[idx]

    return (
      safeTypeNumber(dataRow[this.columns['claims_data.' + claims + '_inc']]) -
      safeTypeNumber(dataRow[this.columns['claims_data.' + claims + '_paid']])
    )
  }

  /**
   * Calculates the total incurred amount for a given index.
   * @param idx - The index of the data row.
   * @returns The total incurred amount.
   */
  incurredTotal(idx: number, normalise: string[] | null = null): number {
    return this.claimsType.reduce((ps: number, claims: string) => ps + this.incurred(idx, claims, normalise), 0)
  }

  /**
   * Calculates the incurred amount for a given index and claims type.
   * @param idx - The index of the data row.
   * @param claims - The type of claims.
   * @returns The incurred amount.
   */
  incurred(idx: number, claims: string, normalise: string[] | null = null): number {
    const dataRow = this.data[idx]

    if (normalise && normalise.includes(claims)) {
      return (
        safeTypeNumber(dataRow[this.columns['uw_data.' + claims + '_MODEL']]) *
        safeTypeNumber(dataRow[this.columns['uw_data.' + claims + '_seasonality']])
      )
    }
    return safeTypeNumber(dataRow[this.columns['claims_data.' + claims + '_inc']])
  }

  /**
   * Calculates the total IBNR amount for a given index.
   * @param idx - The index of the data row.
   * @returns The total IBNR amount.
   */
  ibnrTotal(idx: number): number {
    return this.claimsType.reduce((ps: number, claims: string) => ps + this.ibnr(idx, claims), 0)
  }

  /**
   * Calculates the IBNR amount for a given index and claims type.
   * @param idx - The index of the data row.
   * @param claims - The type of claims.
   * @returns The IBNR amount.
   */
  ibnr(idx: number, claims: string): number {
    const dataRow = this.data[idx]

    return safeTypeNumber(dataRow[this.columns[claims + '_ibnr']])
  }

  /**
   * Calculates the commission for a given index.
   * @param idx - The index of the data row.
   * @returns The commission value.
   */
  commission(idx: number): number {
    const dataRow = this.data[idx]
    return this.commissionColumns
      .map((x: string) => safeTypeNumber(dataRow[this.columns[x]]))
      .reduce((ps: number, s: number) => ps + s, 0)
  }

  /**
   * Calculates the ultimate amount for a given index, underwriting account, underwriting loss ratios, and claims type.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param claims - The type of claims.
   * @returns The ultimate amount.
   */
  ultimate(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    claims: string,
    normalise: string[] | null = null,
    isExcludeIbnr: boolean = false
  ): number {
    const dataRow = this.data[idx]
    const self = this

    function ultimateModel(claims: string, columns: ClaimsColumnLocation): number {
      if (normalise && normalise.includes(claims)) {
        // Match sybil-fe: full MODEL × seasonalityCol (raw, including 0).
        const seas = self.seasonalityValue(idx, claims, uwAcc, underwritingLossRatios)
        return safeTypeNumber(dataRow[columns['uw_data.' + claims + '_MODEL']]) * seas
      } 
      return (
        safeTypeNumber(dataRow[columns['claims_data.' + claims + '_inc']]) +
          (isExcludeIbnr ? 0 : safeTypeNumber(dataRow[columns[claims + '_ibnr']]))
      )
      
    }

    return underwritingLossRatios == 'Written' && uwAcc == 'uw'
      ? safeTypeNumber(dataRow[this.columns[claims + '_unearned_apriori']]) + ultimateModel(claims, this.columns)
      : ultimateModel(claims, this.columns)
  }

  /**
   * Calculates the total ultimate amount for a given index, underwriting account, and underwriting loss ratios.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @returns The total ultimate amount.
   */
  ultimateTotal(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[] | null = null,
    isExcludeIbnr: boolean = false
  ): number {
    return this.claimsType.reduce(
      (ps: number, claims: string) =>
        ps + this.ultimate(idx, uwAcc, underwritingLossRatios, claims, normalise, isExcludeIbnr),
      0
    )
  }

  /**
   * Calculates the NEP amount for a given index.
   * @param idx - The index of the data row.
   * @returns The NEP amount value.
   */
  nepAmount(idx: number): number {
    const dataRow = this.data[idx]
    return (
      safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']]) -
      this.commissionColumns
        .map((x: string) => dataRow[this.columns[x]])
        .reduce((ps: number, s: number | string) => ps + safeTypeNumber(s), 0)
    )
  }

  /**
   * Retrieves the GEP amount for a given index.
   * @param idx - The index of the data row.
   * @returns The GEP amount value.
   */
  gepAmount(idx: number): number {
    const dataRow = this.data[idx]
    return safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']])
  }

  /**
   * Calculates the GWP or NWP amount for a given index and GWP/NWP type.
   * @param idx - The index of the data row.
   * @param gwpNwp - The GWP/NWP type.
   * @returns The GWP or NWP amount value.
   */
  gwpNWPAmount(idx: number, gwpNwp: string): number {
    const dataRow = this.data[idx]

    if (gwpNwp == 'NWP') {
      return (
        safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']]) -
        this.commissionColumns
          .map((x) => safeTypeNumber(dataRow[this.columns[x.replace('uw_data', 'uws')]]))
          .reduce((ps: number, s: number) => ps + s, 0)
      )
    } 
    return safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']])
    
  }

  gwpSumOrGEPAmount(idx: number, underwritingLossRatios: string, uwAcc: string): number {
    const dataRow = this.data[idx]
    return underwritingLossRatios == 'Written' && uwAcc == 'uw'
      ? safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']])
      : safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']])
  }
  /**
   * Calculates the unearned amount for a given index and claims type.
   * @param idx - The index of the data row.
   * @param claims - The type of claims.
   * @returns The unearned amount.
   */
  unearned(idx: number, claims: string): number {
    const dataRow = this.data[idx]
    return safeTypeNumber(dataRow[this.columns[claims + '_unearned_apriori']])
  }

  /**
   * Calculates the total unearned amount for a given index.
   * @param idx - The index of the data row.
   * @returns The total unearned amount.
   */
  unearnedTotal(idx: number): number {
    return this.claimsType.reduce((ps: number, claims: string) => ps + this.unearned(idx, claims), 0)
  }

  /**
   * Calculates the normalized NLR (Net Loss Ratio) for a given index, underwriting account, and underwriting loss ratios.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @returns The normalized NLR value.
   */
  normalisedNLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[],
    _seasonFactor: boolean,
    excludeIbnr: boolean = false
  ): number {
    const isWrittenUw = underwritingLossRatios == 'Written' && uwAcc == 'uw'
    const gep = safeTypeNumber(this.data[idx][this.columns['uw_data.GEP_AMOUNT']])
    const gwp = safeTypeNumber(this.data[idx][this.columns['uws.GWP_SUM']])
    if (isWrittenUw ? gwp == 0 : gep == 0) return 0

    const inc = this.claimsType.reduce(
      (sum, ct) => sum + this.normalisedModelOrIncurred(idx, ct, normalise, uwAcc, underwritingLossRatios),
      0
    )
    const ibnr = this.claimsType.reduce((sum, ct) => {
      if (normalise.includes(ct) || excludeIbnr) return sum
      return sum + safeTypeNumber(this.data[idx][this.columns[ct + '_ibnr']])
    }, 0)

    if (isWrittenUw) {
      const nwp =
        gwp -
        this.commissionColumns
          .map((x) => safeTypeNumber(this.data[idx][this.columns[x.replace('uw_data', 'uws')]]))
          .reduce((ps: number, s: number) => ps + s, 0)
      return safeDivide(inc + ibnr + this.unearnedTotal(idx), nwp)
    }
    const nep = gep - this.commission(idx)
    return safeDivide(inc + ibnr, nep)
  }

  /**
   * Calculates the seasonally adjusted CCR or NLR (Combined Claims Ratio or Net Loss Ratio) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @returns The seasonally adjusted CCR or NLR ratios.
   */

  seasAdjustedCCRNLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    seasonFactor: boolean,
    ccrNlr: string
  ): number {
    return ccrNlr == 'CCR'
      ? this.seasAdjustedCCR(idx, uwAcc, underwritingLossRatios, seasonFactor)
      : this.seasAdjustedNLR(idx, uwAcc, underwritingLossRatios, seasonFactor)
  }

  /**
   * Calculates the seasonally adjusted NLR (Net Loss Ratio) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @returns The seasonally adjusted NLR value.
   */
  seasAdjustedNLR(idx: number, uwAcc: string, underwritingLossRatios: string, seasonFactor: boolean): number {
    const dataRow = this.data[idx]
    const isWrittenUw = underwritingLossRatios == 'Written' && uwAcc == 'uw'
    const divisorBase = isWrittenUw
      ? safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']])
      : safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']])
    if (divisorBase == 0) return 0

    const modelSum = this.claimsType.reduce((sum: number, x: string) => {
      const model = this.effectiveModel(idx, x, uwAcc, underwritingLossRatios)
      const seas = this.appliedSeasonality(idx, x, uwAcc, underwritingLossRatios, seasonFactor)
      return sum + model * seas
    }, 0)

    if (isWrittenUw) {
      const commUws = this.commissionColumns
        .map((c) => safeTypeNumber(dataRow[this.columns[c.replace('uw_data', 'uws')]]))
        .reduce((ps: number, s: number) => ps + s, 0)
      const nwp = safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']]) - commUws
      return nwp != 0 ? modelSum / nwp : 0
    }
    const nep =
      safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']]) -
      this.commissionColumns
        .map((c) => safeTypeNumber(dataRow[this.columns[c]]))
        .reduce((ps: number, s: number) => ps + s, 0)
    return nep != 0 ? modelSum / nep : 0
  }

  private effectiveModel(
    idx: number,
    claims: string,
    uwAcc: string,
    underwritingLossRatios: string
  ): number {
    const dataRow = this.data[idx]
    const model = safeTypeNumber(dataRow[this.columns['uw_data.' + claims + '_MODEL']])
    if (uwAcc === 'uw' && underwritingLossRatios !== 'Written') {
      return model - safeTypeNumber(dataRow[this.columns[claims + '_unearned_apriori']])
    }
    return model
  }

  /**
   * Calculates the seasonally adjusted CCR (Combined Claims Ratio) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @returns The seasonally adjusted CCR value.
   */
  seasAdjustedCCR(idx: number, uwAcc: string, underwritingLossRatios: string, seasonFactor: boolean): number {
    const dataRow = this.data[idx]
    const isWrittenUw = underwritingLossRatios == 'Written' && uwAcc == 'uw'
    const gep = safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']])
    const gwp = safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']])
    if (isWrittenUw ? gwp == 0 : gep == 0) return 0

    const modelSum = this.claimsType.reduce((sum: number, x: string) => {
      const model = this.effectiveModel(idx, x, uwAcc, underwritingLossRatios)
      const seas = this.appliedSeasonality(idx, x, uwAcc, underwritingLossRatios, seasonFactor)
      return sum + model * seas
    }, 0)

    const comm = this.commissionColumns
      .map((x) => safeTypeNumber(dataRow[this.columns[x]]))
      .reduce((ps: number, s: number) => ps + s, 0)

    if (isWrittenUw) {
      return safeDivide(modelSum, gwp) + safeDivide(comm, gep)
    }
    return (modelSum + comm) / gep
  }

  /**
   *  Calculates the seasonally adjusted NEP (Net Earned Premium) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @param claims - The type of claims.
   * @returns - The seasonally adjusted NEP value.
   */

  seasAdjApriori(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    seasonFactor: boolean,
    claims: string
  ): number {
    const model = this.effectiveModel(idx, claims, uwAcc, underwritingLossRatios)
    const seas = this.appliedSeasonality(idx, claims, uwAcc, underwritingLossRatios, seasonFactor)
    return model * seas
  }

  /**
   * Calculates the seasonally adjusted NLR (Net Loss Ratio) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @param claims - The type of claims.
   * @returns - The seasonally adjusted NLR value.
   */

  seasonality(idx: number, uwAcc: string, underwritingLossRatios: string, claims: string): number {
    return this.rawSeasonality(idx, claims, uwAcc, underwritingLossRatios)
  }

  /** Calculates the normalised CCR or NLR (Combined Claims Ratio or Net Loss Ratio) for a given index, underwriting account, underwriting loss ratios, normalise, CCR/NLR type, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param normalise - The normalise value.
   * @param ccrNlr - The CCR/NLR type.
   * @param seasonFactor - The season factor.
   * @returns The normalised CCR or NLR ratios.
   * */

  normalisedCCRNLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[],
    ccrNlr: string,
    seasonFactor: boolean,
    excludeIbnr: boolean = false
  ): number {
    return ccrNlr == 'CCR'
      ? this.normalisedCCR(idx, uwAcc, underwritingLossRatios, normalise, seasonFactor, excludeIbnr)
      : this.normalisedNLR(idx, uwAcc, underwritingLossRatios, normalise, seasonFactor, excludeIbnr)
  }

  /**
   * Calculates the seasonally adjusted CCR (Combined Claims Ratio) for a given index, underwriting account, underwriting loss ratios, and season factor.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param seasonFactor - The season factor.
   * @param claims - The type of claims.
   * @returns - The seasonally adjusted CCR value.
   */

  private normalisedModelOrIncurred(
    idx: number,
    claims: string,
    normalise: string[],
    uwAcc: string,
    underwritingLossRatios: string
  ): number {
    const dataRow = this.data[idx]
    if (!normalise.includes(claims)) {
      return safeTypeNumber(dataRow[this.columns['claims_data.' + claims + '_inc']])
    }
    const model = this.effectiveModel(idx, claims, uwAcc, underwritingLossRatios)
    return model * this.seasonalityValue(idx, claims, uwAcc, underwritingLossRatios)
  }

  normalisedCCR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[],
    _seasonFactor: boolean,
    excludeIbnr: boolean
  ): number {
    const isWrittenUw = underwritingLossRatios == 'Written' && uwAcc == 'uw'
    const gep = safeTypeNumber(this.data[idx][this.columns['uw_data.GEP_AMOUNT']])
    const gwp = safeTypeNumber(this.data[idx][this.columns['uws.GWP_SUM']])
    if (isWrittenUw ? gwp == 0 : gep == 0) return 0

    const inc = this.claimsType.reduce(
      (sum, ct) => sum + this.normalisedModelOrIncurred(idx, ct, normalise, uwAcc, underwritingLossRatios),
      0
    )
    const ibnr = this.claimsType.reduce((sum, ct) => {
      if (normalise.includes(ct) || excludeIbnr) return sum
      return sum + safeTypeNumber(this.data[idx][this.columns[ct + '_ibnr']])
    }, 0)
    const comm = this.commission(idx)

    if (isWrittenUw) {
      return safeDivide(inc + ibnr + this.unearnedTotal(idx), gwp) + safeDivide(comm, gep)
    }
    return safeDivide(inc + ibnr + comm, gep)
  }

  /**
   * Calculates the CCR or NLR (Combined Claims Ratio or Net Loss Ratio) for a given index, underwriting account, underwriting loss ratios, and CCR/NLR type.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @param ccrNlr - The CCR/NLR type.
   * @returns The CCR or NLR ratios.
   */

  ccrNlr(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    ccrNlr: string,
    excludeIbnr: boolean = false
  ): number {
    return ccrNlr == 'CCR'
      ? this.ccr(idx, uwAcc, underwritingLossRatios, excludeIbnr)
      : this.nlr(idx, uwAcc, underwritingLossRatios, excludeIbnr)
  }

  /**
   * Calculates the CCR (Combined Claims Ratio) for a given index, underwriting account, and underwriting loss ratios.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @returns The CCR ratios.
   */
  ccr(idx: number, uwAcc: string, underwritingLossRatios: string, excludeIbnr: boolean = false): number {
    let ans = 0
    const dataRow = this.data[idx]

    // Add basic logging to see if this method is called

    // Add detailed logging for recent months (last 12 months)
    const totalRows = Object.keys(this.data).length
    const isRecentMonth = idx >= (totalRows - 12)
    

    dataRow[this.columns['uw_data.GEP_AMOUNT']] != 0
      ? underwritingLossRatios == 'Written' && uwAcc == 'uw'
        ? (ans =
            (this.claimsType
              .map((x) => safeTypeNumber(dataRow[this.columns['claims_data.' + x + '_inc']]))
              .reduce((ps: number, s: number) => ps + s, 0) +
              this.claimsType
                .map((x) => (excludeIbnr ? 0 : safeTypeNumber(dataRow[this.columns[x + '_ibnr']])))
                .reduce((ps: number, s: number) => ps + s, 0) +
              this.unearnedTotal(idx)) /
              safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']]) +
            this.commissionColumns
              .map((x) => safeTypeNumber(dataRow[this.columns[x]]))
              .reduce((ps: number, s: number) => ps + s, 0) /
              safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']]))
        : (ans =
            (this.claimsType
              .map((x) => safeTypeNumber(dataRow[this.columns['claims_data.' + x + '_inc']]))
              .reduce((ps: number, s: number) => ps + s, 0) +
              this.claimsType
                .map((x) => (excludeIbnr ? 0 : safeTypeNumber(dataRow[this.columns[x + '_ibnr']])))
                .reduce((ps: number, s: number) => ps + s, 0) +
              this.commissionColumns
                .map((x) => safeTypeNumber(dataRow[this.columns[x]]))
                .reduce((ps: number, s: number) => ps + s, 0)) /
            safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']]))
      : (ans = 0)
    
    
    return ans
  }

  /**
   * Calculates the NLR (Net Loss Ratio) for a given index, underwriting account, and underwriting loss ratios.
   * @param idx - The index of the data row.
   * @param uwAcc - Underwriting Or Accident
   * @param underwritingLossRatios - The underwriting loss ratios.
   * @returns The NLR ratios.
   */
  nlr(idx: number, uwAcc: string, underwritingLossRatios: string, excludeIbnr: boolean = false): number {
    let ans = 0
    const dataRow = this.data[idx]

    // Add basic logging to see if this method is called

    // Add detailed logging for recent months (last 12 months)
    const totalRows = Object.keys(this.data).length
    const isRecentMonth = idx >= (totalRows - 12)
    

    dataRow[this.columns['uw_data.GEP_AMOUNT']] != 0
      ? underwritingLossRatios == 'Written' && uwAcc == 'uw'
        ? (ans =
            (this.claimsType
              .map((x) => safeTypeNumber(dataRow[this.columns['claims_data.' + x + '_inc']]))
              .reduce((ps: number, s: number) => ps + s, 0) +
              this.claimsType
                .map((x) => (excludeIbnr ? 0 : safeTypeNumber(dataRow[this.columns[x + '_ibnr']])))
                .reduce((ps: number, s: number) => ps + s, 0) +
              this.unearnedTotal(idx)) /
            (safeTypeNumber(dataRow[this.columns['uws.GWP_SUM']]) -
              this.commissionColumns
                .map((x) => safeTypeNumber(dataRow[this.columns[x.replace('uw_data', 'uws')]]))
                .reduce((ps: number, s: number) => ps + s, 0)))
        : (ans =
            (this.claimsType
              .map((x) => safeTypeNumber(dataRow[this.columns['claims_data.' + x + '_inc']]))
              .reduce((ps: number, s: number) => ps + s, 0) +
              this.claimsType
                .map((x) => (excludeIbnr ? 0 : safeTypeNumber(dataRow[this.columns[x + '_ibnr']])))
                .reduce((ps: number, s: number) => ps + s, 0)) /
            (safeTypeNumber(dataRow[this.columns['uw_data.GEP_AMOUNT']]) -
              this.commissionColumns
                .map((x) => safeTypeNumber(dataRow[this.columns[x]]))
                .reduce((ps: number, s: number) => ps + s, 0)))
      : (ans = 0)
    
    
    return ans
  }

  // --- Shared helpers / GLR family (ported from sybil-fe for chart parity) ---

  private val(idx: number, col: string): number {
    return safeTypeNumber(this.data[idx]?.[this.columns[col]])
  }

  private commissionUws(idx: number): number {
    return this.commissionColumns.reduce(
      (sum, col) => sum + this.val(idx, col.replace('uw_data', 'uws')),
      0
    )
  }

  private totalIncurred(idx: number): number {
    return this.claimsType.reduce((sum, ct) => sum + this.val(idx, 'claims_data.' + ct + '_inc'), 0)
  }

  private totalIbnr(idx: number): number {
    return this.claimsType.reduce((sum, ct) => sum + this.val(idx, ct + '_ibnr'), 0)
  }

  private totalModel(idx: number, uwAcc: string, underwritingLossRatios: string): number {
    return this.claimsType.reduce(
      (sum, ct) => sum + this.effectiveModel(idx, ct, uwAcc, underwritingLossRatios),
      0
    )
  }

  private modelSumWithSeasonality(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    seasonFactor: boolean,
    normalise: string[] | null
  ): number {
    return this.claimsType.reduce((sum, ct) => {
      if (normalise && !normalise.includes(ct)) {
        return sum + this.val(idx, 'claims_data.' + ct + '_inc')
      }
      const model = this.effectiveModel(idx, ct, uwAcc, underwritingLossRatios)
      const seas = this.appliedSeasonality(idx, ct, uwAcc, underwritingLossRatios, seasonFactor)
      return sum + model * seas
    }, 0)
  }

  private normalisedIbnr(idx: number, normalise: string[], excludeIbnr: boolean): number {
    return this.claimsType.reduce((sum, ct) => {
      if (normalise.includes(ct) || excludeIbnr) return sum
      return sum + this.val(idx, ct + '_ibnr')
    }, 0)
  }

  private writtenUwGlrAdjustment(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string
  ): number {
    const gwp = this.val(idx, 'uws.GWP_SUM')
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gwp === 0 || gep === 0) return 0
    return safeDivide(this.totalModel(idx, uwAcc, underwritingLossRatios), gep) * (gwp - gep)
  }

  glr(idx: number, uwAcc: string, underwritingLossRatios: string): number {
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gep === 0) return 0
    const isWrittenUw = underwritingLossRatios === 'Written' && uwAcc === 'uw'
    if (isWrittenUw) {
      const gwp = this.val(idx, 'uws.GWP_SUM')
      if (gwp === 0) return 0
      return safeDivide(
        safeDivide(
          this.totalIncurred(idx) +
            this.totalIbnr(idx) +
            this.writtenUwGlrAdjustment(idx, uwAcc, underwritingLossRatios),
          gwp
        ),
        gep
      )
    }
    return safeDivide(this.totalIncurred(idx) + this.totalIbnr(idx), gep)
  }

  normalisedGLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[],
    seasonFactor: boolean
  ): number {
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gep === 0) return 0
    const isWrittenUw = underwritingLossRatios === 'Written' && uwAcc === 'uw'
    const numerator =
      this.modelSumWithSeasonality(idx, uwAcc, underwritingLossRatios, seasonFactor, normalise) +
      this.normalisedIbnr(idx, normalise, false)
    if (isWrittenUw) {
      const gwp = this.val(idx, 'uws.GWP_SUM')
      if (gwp === 0) return 0
      return safeDivide(
        safeDivide(numerator + this.writtenUwGlrAdjustment(idx, uwAcc, underwritingLossRatios), gwp),
        gep
      )
    }
    return safeDivide(numerator, gep)
  }

  notNormalisedIncurredGLR(idx: number, uwAcc: string, underwritingLossRatios: string): number {
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gep === 0) return 0
    const isWrittenUw = underwritingLossRatios === 'Written' && uwAcc === 'uw'
    if (isWrittenUw) {
      const gwp = this.val(idx, 'uws.GWP_SUM')
      if (gwp === 0) return 0
      return safeDivide(
        safeDivide(
          this.totalIncurred(idx) + this.writtenUwGlrAdjustment(idx, uwAcc, underwritingLossRatios),
          gwp
        ),
        gep
      )
    }
    return safeDivide(this.totalIncurred(idx), gep)
  }

  normalisedIncurredGLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    normalise: string[],
    seasonFactor: boolean
  ): number {
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gep === 0) return 0
    const isWrittenUw = underwritingLossRatios === 'Written' && uwAcc === 'uw'
    const numerator = this.modelSumWithSeasonality(
      idx,
      uwAcc,
      underwritingLossRatios,
      seasonFactor,
      normalise
    )
    if (isWrittenUw) {
      const gwp = this.val(idx, 'uws.GWP_SUM')
      if (gwp === 0) return 0
      return safeDivide(
        safeDivide(numerator + this.writtenUwGlrAdjustment(idx, uwAcc, underwritingLossRatios), gwp),
        gep
      )
    }
    return safeDivide(numerator, gep)
  }

  seasAdjAprioriGLR(
    idx: number,
    uwAcc: string,
    underwritingLossRatios: string,
    seasonFactor: boolean
  ): number {
    const gep = this.val(idx, 'uw_data.GEP_AMOUNT')
    if (gep === 0) return 0
    const modelSum = this.claimsType.reduce((sum, ct) => {
      const model = this.effectiveModel(idx, ct, uwAcc, underwritingLossRatios)
      const seas = this.appliedSeasonality(idx, ct, uwAcc, underwritingLossRatios, seasonFactor)
      return sum + model * seas
    }, 0)
    return safeDivide(modelSum, gep)
  }
}
